import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ArrowRight, Boxes, Dices, Download, Gamepad2, Gem, Joystick,
  Loader2, Network, PlugZap, RadioTower, RefreshCw, Settings2, ShieldOff,
  Spade, Sparkles, Swords, Trophy, Unplug, WifiOff,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import GameApiSettingsPanel from './GameApiSettingsPanel';
import FlexCasinoSettingsPanel from './FlexCasinoSettingsPanel';
import FlexGisSettingsPanel from './FlexGisSettingsPanel';
import ViperCasinoSettingsPanel from './ViperCasinoSettingsPanel';
import GameSystemSettingsPanel from './GameSystemSettingsPanel';
import GameWinsSettingsPanel from './GameWinsSettingsPanel';
import GameLobbySettingsPanel from './GameLobbySettingsPanel';
import SportsControlPanel from './SportsControlPanel';
import './gameControl.css';

/*
 * Sağlayıcının görünen kimliği: ikon, ton rengi ve bir cümlelik tanım.
 *
 * Ton, ayar panelindeki `SERVICES[*].accent` ile **aynı olmalıdır**
 * (GameApiSettingsPanel): yönetici kartı açıp panele geçtiğinde renk
 * değişirse iki ekran iki ayrı sağlayıcıymış gibi görünür. Ayrışmaması için
 * yeni sağlayıcı eklerken iki yeri birlikte yazın.
 */
const PROVIDER_META = {
  drakon: {
    icon: Dices,
    tone: '#38bdf8',
    summary: 'Seamless cüzdan. Slot ve canlı casino kataloğu; demo açılışını bugün yalnızca bu kaynak ve Flexrix Casino verir.',
  },
  goldpalace: {
    icon: Spade,
    tone: '#22c55e',
    summary: 'Seamless cüzdan. Slot ve canlı casino oyun açılışları; oyuncu bazlı freespin tanımlanabilir.',
  },
  oroplay: {
    icon: Gem,
    tone: '#f472b6',
    summary: 'Seamless cüzdan. Slot, canlı casino ve mini oyun katalogları; demo ve freespin sunmaz.',
  },
  flexcasino: {
    icon: Sparkles,
    tone: '#a78bfa',
    summary: 'Seamless cüzdan. Flexrix’in native slot, masa ve crash oyunları; demo cüzdansız açılır.',
  },
  flexgis: {
    icon: Boxes,
    tone: '#f59e0b',
    summary: 'Seamless cüzdan. Flexrix’in agregasyon kataloğu: Evolution ve diğer üreticiler, canlı masa lobisi. Kimlik ve callback adresi native üründen ayrıdır.',
  },
  viper: {
    icon: Swords,
    tone: '#84cc16',
    summary: 'Seamless cüzdan. Viper GGR’ın slot ve canlı casino kataloğu; demo vermez. API bilgileri ve cüzdan adresi Viper Sportsbook ile ortaktır, sunucu IP’si Viper’ın beyaz listesinde olmalıdır.',
  },
  scorpio: {
    icon: Joystick,
    tone: '#64748b',
    summary: 'Transfer cüzdan. Açılışta bakiye sağlayıcıya aktarılır, kapanışta geri alınır.',
  },
};

const providerMeta = (id) => PROVIDER_META[id] || {
  icon: Gamepad2, tone: '#8f8fa0', summary: '',
};

// Katalog senkronizasyonunun uçları sağlayıcıya göre farklıdır; ScorpioPlay
// pasif olduğu için hiç senkronize edilmez (listede yer almaz).
const syncEndpoints = {
  drakon: {
    providers: '/admin/drakon/sync-providers',
    games: '/admin/drakon/sync',
  },
  goldpalace: {
    providers: '/admin/games/goldpalace-sync-providers',
    games: '/admin/games/goldpalace-sync',
  },
  oroplay: {
    providers: '/admin/games/oroplay-sync-vendors',
    games: '/admin/games/oroplay-sync',
  },
  // Ayarları ``provider_configs``'te durduğu için uçları da kendi
  // ön ekindedir (bkz. backend/routers/flexcasino_router.py).
  flexcasino: {
    providers: '/admin/flexcasino/sync-providers',
    games: '/admin/flexcasino/sync',
  },
  // Agregasyon kataloğunun **ayrı bir üretici ucu yoktur**: üretici satırları
  // katalog senkronu sırasında doğar, bu yüzden burada yalnızca oyun ucu
  // vardır ve panel "Sağlayıcıları Çek" düğmesini bu kaynakta hiç çizmez.
  flexgis: {
    games: '/admin/flexgis/sync',
  },
  // Viper'ın da ayrı bir üretici ucu yoktur; üreticiler katalogdan doğar.
  viper: {
    games: '/admin/viper/sync',
  },
};

// Yapılandırması ``settings`` tablosunda değil ``provider_configs`` satırında
// duran kaynaklar: ayar panelleri de ayrıdır.
const CONFIG_BACKED_PANELS = {
  flexcasino: FlexCasinoSettingsPanel,
  flexgis: FlexGisSettingsPanel,
  viper: ViperCasinoSettingsPanel,
};

const TABS = [
  { id: 'services', label: 'Oyun Servisleri', icon: Gamepad2 },
  { id: 'system', label: 'Gateway & Genel', icon: Network },
  { id: 'sports', label: 'Spor & Cashout', icon: Trophy },
];

// Sayı biçimi tek yerdedir: kartlardaki ölçüler binlik ayracıyla okunur
// (15165 değil 15.165) ve dört kutu aynı hizada durur.
const formatCount = (value) => Number(value || 0).toLocaleString('tr-TR');

// Yeni panel tasarımında sayfa bölümlere ayrılır (Casino → Oyun Servisleri,
// Casino → Oyun Ayarları): `lockedSection` verilince sekme şeridi çizilmez ve
// başlık o bölümün adını taşır. Verilmezse klasik görünümün üç sekmeli
// sayfası olduğu gibi kalır.
const LOCKED_HEADINGS = {
  services: {
    title: 'Oyun Servisleri',
    desc: 'Casino sağlayıcılarının bağlantısı, açma/kapama ve katalog durumu.',
  },
  system: {
    title: 'Oyun Ayarları',
    desc: 'Gateway, oyun içi lobi düğmesi ve oyun sayfasındaki son kazançlar.',
  },
};

const LiveCasinoControl = ({ lockedSection } = {}) => {
  const locked = LOCKED_HEADINGS[lockedSection] ? lockedSection : null;
  const [control, setControl] = useState(null);
  const [connections, setConnections] = useState({});
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [settingsService, setSettingsService] = useState('');
  const [section, setSection] = useState(locked || 'services');
  const [error, setError] = useState('');
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [
      controlResult, servicesResult, scorpioResult, drakonResult, goldpalaceResult,
      oroplayResult, flexcasinoResult, flexgisResult, viperResult,
    ] = await Promise.allSettled([
      client.get('/admin/games/live-casino-control'),
      client.get('/admin/games/services'),
      client.get('/admin/games/api-status'),
      client.get('/admin/drakon/api-status'),
      client.get('/admin/games/goldpalace-status'),
      client.get('/admin/games/oroplay-status'),
      client.get('/admin/flexcasino/status'),
      client.get('/admin/flexgis/status'),
      client.get('/admin/viper/status'),
    ]);

    if (controlResult.status === 'fulfilled') {
      setControl(controlResult.value.data);
    } else {
      setError(
        controlResult.reason?.response?.data?.detail
          || 'Canlı casino kontrol bilgileri alınamadı',
      );
    }
    setServices(
      servicesResult.status === 'fulfilled'
        ? (servicesResult.value.data.items || [])
        : [],
    );
    setConnections({
      scorpio: scorpioResult.status === 'fulfilled'
        ? scorpioResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      drakon: drakonResult.status === 'fulfilled'
        ? drakonResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      goldpalace: goldpalaceResult.status === 'fulfilled'
        ? goldpalaceResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      oroplay: oroplayResult.status === 'fulfilled'
        ? oroplayResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      flexcasino: flexcasinoResult.status === 'fulfilled'
        ? flexcasinoResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      flexgis: flexgisResult.status === 'fulfilled'
        ? flexgisResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
      viper: viperResult.status === 'fulfilled'
        ? viperResult.value.data
        : { connected: false, error: 'Bağlantı durumu alınamadı' },
    });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /*
   * Ayar formu kartın altında değil sağdan açılan bir çekmecede yaşar: altı
   * sağlayıcı kartının altına açılan panel, üçüncü karta basan yöneticiyi
   * sayfanın dibine gönderiyor ve hangi sağlayıcıyı düzenlediği ekranda
   * görünmüyordu. Çekmece açıkken Esc kapatır ve sayfanın kendi kaydırması
   * kilitlenir — iki kaydırma çubuğu yan yana düşünce form kaydırılamıyordu.
   */
  useEffect(() => {
    if (!settingsService) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setSettingsService('');
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Panel asistanının kenar sekmesi sayfanın en üst katmanındadır ve tam
    // sağda durur: çekmecenin sağ kenarına biniyor, formun alanını örtüyordu.
    document.body.classList.add('gc-drawer-open');
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.body.classList.remove('gc-drawer-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [settingsService]);

  const toggleSource = async (source) => {
    const nextEnabled = !source.enabled;
    if (!nextEnabled && source.active_sessions > 0) {
      const accepted = window.confirm(
        `${source.name} üzerinde ${source.active_sessions} aktif canlı casino oturumu var. `
        + 'Kaynak kapatıldığında yeni oyun açılamaz; mevcut oturumları Oyun Yönetimi sayfasından kapatabilirsiniz. Devam edilsin mi?',
      );
      if (!accepted) return;
    }

    setBusy(source.id);
    try {
      const { data } = await client.patch(
        `/admin/games/live-casino-control/${source.id}`,
        { enabled: nextEnabled },
      );
      setControl(data);
      toast({
        title: `${source.name} canlı casino ${nextEnabled ? 'açıldı' : 'kapatıldı'}`,
        description: nextEnabled
          ? 'Oyunlar vitrinde görünür ve yeni açılışlara izin verilir.'
          : 'Oyunlar vitrinden kaldırıldı ve yeni açılışlar engellendi.',
      });
    } catch (requestError) {
      toast({
        title: 'Durum güncellenemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  // Servisin tamamı: kapatıldığında kaynağın hiçbir oyunu siteye çıkmaz.
  const toggleService = async (source, service) => {
    const nextEnabled = !service.enabled;
    setBusy(`${source.id}-service`);
    try {
      const { data } = await client.patch(`/admin/games/services/${source.id}`, {
        enabled: nextEnabled,
      });
      toast({
        title: `${source.name} servisi ${data.enabled ? 'açıldı' : 'kapatıldı'}`,
        description: data.enabled
          ? 'Katalog ve oyun açılışlarına yeniden dahil edildi.'
          : 'Kaynağın tüm oyunları siteden gizlendi ve açılışlar engellendi.',
      });
      await load();
    } catch (requestError) {
      toast({
        title: 'Servis durumu değiştirilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  const toggleServiceCategory = async (source, category, currentEnabled) => {
    setBusy(`${source.id}-${category}`);
    try {
      const { data } = await client.patch(
        `/admin/games/services/${source.id}/category/${category}`,
        { enabled: !currentEnabled },
      );
      const categoryName = category === 'slot' ? 'Slot' : 'Casino';
      toast({
        title: `${categoryName} ${data.policy_enabled ? 'açıldı' : 'kapatıldı'}`,
        description: `${source.name} kaynağındaki ${categoryName.toLowerCase()} oyunları ${
          data.policy_enabled ? 'siteye dahil edildi' : 'siteden gizlendi'
        }`,
      });
      await load();
    } catch (requestError) {
      toast({
        title: 'Kategori durumu değiştirilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  /** Katalog senkronizasyonu. kind: providers | games | live */
  const syncCatalog = async (source, kind) => {
    const endpoints = syncEndpoints[source.id];
    if (!endpoints) return;
    const path = kind === 'providers' ? endpoints.providers : endpoints.games;
    // Kaynağın o ucu yoksa düğme zaten çizilmez; yine de boş adrese istek
    // atılmaz — sessizce hata veren bir düğme yöneticiye sorunu sayfada
    // sandırır.
    if (!path) return;
    setBusy(`${source.id}-${kind}`);
    try {
      const { data } = await client.post(
        path,
        null,
        kind === 'live' ? { params: { only_live: true } } : undefined,
      );
      const counts = [`${data.created ?? 0} yeni`, `${data.updated ?? 0} güncellendi`];
      if (data.skipped) counts.push(`${data.skipped} atlandı`);
      if (data.failed_providers) counts.push(`${data.failed_providers} sağlayıcı hatalı`);
      toast({
        title: `${source.name}: ${
          kind === 'providers'
            ? 'sağlayıcılar'
            : kind === 'live' ? 'canlı casino oyunları' : 'oyunlar'
        } senkronize edildi`,
        description: counts.join(', '),
      });
      if (data.errors?.length) {
        toast({
          title: 'Bazı sağlayıcılar alınamadı',
          description: data.errors.map((item) => item.provider || item.source).join(', '),
        });
      }
      await load();
    } catch (requestError) {
      toast({
        title: 'Senkronizasyon başarısız',
        description: requestError.response?.data?.detail || 'Katalog alınamadı',
      });
    } finally {
      setBusy('');
    }
  };

  // Tüm kaynakları Türkiye erişimi için tarar; yalnızca pasife alır, açmaz.
  const deactivateTrUnavailable = async () => {
    const accepted = window.confirm(
      'Tüm sağlayıcı katalogları Türkiye erişimi için taransın ve yalnızca '
      + 'erişilemediği doğrulanan oyunlar pasife alınsın mı?',
    );
    if (!accepted) return;
    setAvailabilityBusy(true);
    try {
      const { data } = await client.post('/admin/games/availability/tr/deactivate');
      toast({
        title: 'Türkiye erişim kontrolü tamamlandı',
        description: `${data.scanned} oyun tarandı · ${data.deactivated} oyun pasife alındı`,
      });
      if (data.errors?.length) {
        toast({
          title: 'Bazı sağlayıcılar kontrol edilemedi',
          description: data.errors
            .map((item) => `${item.source}${item.provider_id ? ` #${item.provider_id}` : ''}`)
            .join(', '),
        });
      }
      await load();
    } catch (requestError) {
      toast({
        title: 'Türkiye erişim kontrolü başarısız',
        description: requestError.response?.data?.detail || 'İşlem tamamlanamadı',
      });
    } finally {
      setAvailabilityBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="gc">
        <div className="gc-state">
          <Loader2 size={26} className="gc-spin" />
          <h2>Oyun kontrolü yükleniyor</h2>
          <p>Servis durumları, sağlayıcı bağlantıları ve katalog sayıları alınıyor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gc">
      <header className="gc-head">
        <span className="gc-head-icon"><RadioTower size={22} /></span>
        <div className="gc-head-text">
          <h1>{locked ? LOCKED_HEADINGS[locked].title : 'Oyun Kontrol'}</h1>
          <p>
            {locked
              ? LOCKED_HEADINGS[locked].desc
              : 'Oyun servislerini, gateway ayarlarını, canlı casinoyu ve sporu tek yerden yönetin.'}
          </p>
        </div>
        <button type="button" onClick={load} className="gc-btn">
          <RefreshCw size={14} /> Yenile
        </button>
      </header>

      {!locked && (
      <nav className="gc-tabs" aria-label="Oyun kontrol bölümleri">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSection(item.id)}
            aria-current={section === item.id ? 'page' : undefined}
            className={`gc-tab ${section === item.id ? 'is-active' : ''}`}
          >
            <item.icon size={15} /> {item.label}
          </button>
        ))}
      </nav>
      )}

      {section === 'services' && (error ? (
        <div className="gc-state" role="alert">
          <WifiOff size={26} color="#fb7185" />
          <h2>Kontrol bilgileri alınamadı</h2>
          <p>{error}</p>
          <button type="button" onClick={load} className="gc-btn">
            <RefreshCw size={14} /> Yeniden Dene
          </button>
        </div>
      ) : (
        <>
          <div className="gc-svc-grid">
            {(control?.sources || []).map((source) => {
              const connection = connections[source.id] || {};
              const service = services.find((item) => item.id === source.id) || null;
              const meta = providerMeta(source.id);
              const ProviderIcon = meta.icon;
              const serviceOn = !!service?.enabled;
              const connected = !!connection.connected;
              const canSync = !!syncEndpoints[source.id] && connected && serviceOn;
              // Senkron butonu kapalıysa sebebini söyle: aksi hâlde yönetici
              // tıklıyor, hiçbir şey olmuyor ve sorunu sayfada sanıyor.
              const syncHint = syncEndpoints[source.id]
                ? (!connected
                  ? 'API bağlı değil — önce Ayarlar bölümünden bağlantı bilgilerini girin'
                  : (!serviceOn
                    ? 'Servis kapalı — önce servisi açın'
                    : ''))
                : 'Bu kaynak için senkronizasyon kapalı';
              // Kartın tek cümlelik durumu. Üç hâl ayrılır çünkü çaresi de
              // ayrıdır: kapalı servis panelden açılır, bağlantısız servis
              // ayar formunu ister, yayındaki servis hiçbir şey istemez.
              const status = !serviceOn
                ? { tone: 'off', label: 'Servis kapalı' }
                : (!connected
                  ? { tone: 'warn', label: 'Bağlantı yok' }
                  : { tone: 'on', label: 'Yayında' });
              const settingsOpen = settingsService === source.id;
              // Kaynağın canlı casino anahtarı **tek** anahtardır: kartta
              // eskiden "Casinoyu Aç" ve "Canlı Casino'yu Aç" diye iki düğme
              // vardı ve ikisi de aynı ayarı (live_casino_<kaynak>_enabled)
              // yazıyordu — yönetici hangisinin ne yaptığını ayırt edemiyordu.
              // Kalan yol toggleSource'tur: açarken üst servis anahtarını da
              // açtığı için kapalı bir serviste de çalışır.
              const toggles = [
                {
                  key: 'service',
                  label: 'Servis',
                  note: 'Kapalıyken kaynağın hiçbir oyunu siteye çıkmaz.',
                  checked: serviceOn,
                  disabled: !service || busy === `${source.id}-service`,
                  onChange: () => service && toggleService(source, service),
                },
                ...(service && 'slots_enabled' in service ? [{
                  key: 'slot',
                  label: 'Slot oyunları',
                  note: 'Kaynağın slot kataloğu vitrinde görünür.',
                  checked: !!service.slots_enabled,
                  disabled: !serviceOn || busy === `${source.id}-slot`,
                  onChange: () => toggleServiceCategory(source, 'slot', service.slots_enabled),
                }] : []),
                {
                  key: 'live',
                  label: 'Canlı casino',
                  note: 'Kaynağın canlı casino oyunları vitrinde görünür.',
                  checked: !!source.enabled,
                  disabled: busy === source.id,
                  onChange: () => toggleSource(source),
                },
              ];
              return (
                <article
                  key={source.id}
                  className={`gc-svc is-${status.tone}`}
                  style={{ '--gc-tone': meta.tone }}
                >
                  <header className="gc-svc-head">
                    <span className="gc-svc-icon"><ProviderIcon size={20} /></span>
                    <div className="gc-svc-id">
                      <h2>{source.name}</h2>
                      <p>
                        <span>{source.wallet_model}</span>
                        <code>{source.id}</code>
                      </p>
                    </div>
                    <span className={`gc-svc-state is-${status.tone}`}>
                      <i aria-hidden="true" />
                      {status.label}
                    </span>
                  </header>

                  <p className="gc-svc-desc">{meta.summary}</p>

                  <div className={`gc-svc-link ${connected ? 'is-ok' : 'is-warn'}`}>
                    {connected ? <PlugZap size={14} /> : <Unplug size={14} />}
                    <span className="gc-svc-link-text">
                      {connected ? 'API bağlı' : 'API bağlı değil'}
                      {connected && connection.provider_count != null
                        && ` · ${formatCount(connection.provider_count)} üretici`}
                      {!connected && connection.error && ` · ${connection.error}`}
                    </span>
                    {connected && connection.base_url && (
                      <code className="gc-svc-link-url">{connection.base_url}</code>
                    )}
                  </div>

                  <dl className="gc-svc-metrics">
                    {[
                      ['Toplam', source.total_games],
                      ['Aktif', source.active_games],
                      ['Vitrinde', source.visible_games],
                      ['Oturum', source.active_sessions],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className={`gc-svc-metric ${Number(value || 0) === 0 ? 'is-zero' : ''}`}
                      >
                        <dt>{label}</dt>
                        <dd>{formatCount(value)}</dd>
                      </div>
                    ))}
                  </dl>

                  <div className="gc-svc-toggles">
                    {toggles.map((item) => (
                      <div
                        key={item.key}
                        className={`gc-svc-toggle ${item.checked ? 'is-on' : ''} ${
                          item.disabled ? 'is-locked' : ''
                        }`}
                      >
                        <div className="gc-svc-toggle-text">
                          <b>{item.label}</b>
                          <small>{item.note}</small>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={item.checked}
                          aria-label={`${source.name} · ${item.label}`}
                          disabled={item.disabled}
                          onClick={item.onChange}
                          className="gc-switch"
                        >
                          <span className="gc-switch-track" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <footer className="gc-svc-foot">
                    <button
                      type="button"
                      onClick={() => setSettingsService(settingsOpen ? '' : source.id)}
                      aria-expanded={settingsOpen}
                      className="gc-btn is-primary is-sm"
                    >
                      <Settings2 size={13} /> Ayarlar
                    </button>
                    {/* Ayrı bir üretici ucu olmayan kaynakta düğme hiç
                        çizilmez: üretici satırları katalog senkronu sırasında
                        doğar ve boş bir adrese POST atan düğme sessizce hata
                        verirdi. */}
                    {syncEndpoints[source.id]?.providers && (
                      <button
                        type="button"
                        onClick={() => syncCatalog(source, 'providers')}
                        disabled={!!busy || !canSync}
                        title={syncHint}
                        className="gc-btn is-sm"
                      >
                        {busy === `${source.id}-providers`
                          ? <Loader2 size={13} className="gc-spin" />
                          : <Download size={13} />}
                        Üreticiler
                      </button>
                    )}
                    {syncEndpoints[source.id]?.games && (
                      <button
                        type="button"
                        onClick={() => syncCatalog(source, 'games')}
                        disabled={!!busy || !canSync}
                        title={syncHint}
                        className="gc-btn is-sm"
                      >
                        {busy === `${source.id}-games`
                          ? <Loader2 size={13} className="gc-spin" />
                          : <Download size={13} />}
                        Oyun kataloğu
                      </button>
                    )}
                    {source.id === 'drakon' && (
                      <button
                        type="button"
                        onClick={() => syncCatalog(source, 'live')}
                        disabled={!!busy || !canSync}
                        title={syncHint}
                        className="gc-btn is-sm"
                      >
                        {busy === `${source.id}-live`
                          ? <Loader2 size={13} className="gc-spin" />
                          : <Activity size={13} />}
                        Yalnızca canlı
                      </button>
                    )}
                  </footer>

                  {syncHint && <p className="gc-svc-warn">{syncHint}</p>}
                </article>
              );
            })}
          </div>

          {/* Çekmece: form sağdan açılır ve hangi sağlayıcıya ait olduğu
              başlığında durur. Zemine tıklamak kapatır; Esc de kapatır
              (yukarıdaki efekt). */}
          {settingsService && (
            <div className="gc-drawer" role="dialog" aria-modal="true">
              <button
                type="button"
                className="gc-drawer-veil"
                aria-label="Ayarları kapat"
                onClick={() => setSettingsService('')}
              />
              <div className="gc-drawer-panel">
                {CONFIG_BACKED_PANELS[settingsService]
                  ? React.createElement(CONFIG_BACKED_PANELS[settingsService], {
                    onClose: () => setSettingsService(''),
                    onSaved: load,
                  })
                  : (
                    <GameApiSettingsPanel
                      serviceId={settingsService}
                      onClose={() => setSettingsService('')}
                      onSaved={load}
                    />
                  )}
              </div>
            </div>
          )}

          <section className="gc-callout is-warn">
            <span className="gc-callout-icon"><ShieldOff size={18} /></span>
            <div className="gc-callout-text">
              <h3>Türkiye Oyun Erişim Kontrolü</h3>
              <p>
                Tüm bağlı sağlayıcıları tarar; TR kataloğunda bulunmayan, launch erişimi kapalı
                veya Türkiye kısıtı bulunan oyunları pasife alır. Erişilebilir oyunları açmaz.
              </p>
            </div>
            <button
              type="button"
              onClick={deactivateTrUnavailable}
              disabled={availabilityBusy}
              className="gc-btn is-warn"
            >
              {availabilityBusy
                ? <Loader2 size={14} className="gc-spin" />
                : <ShieldOff size={14} />}
              {availabilityBusy ? 'Tüm Oyunlar Taranıyor' : 'TR’de Kapalı Oyunları Pasife Al'}
            </button>
          </section>

          <section className="gc-callout">
            <span className="gc-callout-icon"><Gamepad2 size={18} /></span>
            <div className="gc-callout-text">
              <h3>Oyun ve oturum detayları</h3>
              <p>
                Tekil oyun durumlarını ve halen açık oturumları Oyun Yönetimi sayfasından yönetin.
              </p>
            </div>
            <Link to="/admin/games" className="gc-btn">
              Oyun Yönetimine Git <ArrowRight size={14} />
            </Link>
          </section>
        </>
      ))}

      {section === 'system' && (
        <>
          <GameSystemSettingsPanel />
          <GameWinsSettingsPanel />
          <GameLobbySettingsPanel />
        </>
      )}
      {section === 'sports' && <SportsControlPanel />}
    </div>
  );
};

export default LiveCasinoControl;
